import type { FC } from "react";
import { useMemo } from "react";

import { useDeleteDocument, usePatientDocuments } from "@/api/documents";
import { PdfPreview } from "@/components/document/PdfPreview";
import EmptyImageCard from "@/components/layout/empty-image-card";
import DocumentCard from "@/components/patient/document-card";
import { DataTable, useTable } from "@/components/table/data-table";
import { getDocumentColumns } from "@/components/table/patients/document-columns";
import type { Patient } from "@/types/api/patient";

type LastDocumentsProps = {
  patient: Patient;
  isImages: boolean;
  isTable: boolean;
  isPeriotools: boolean;
};

const TableComponent: FC<{
  elements: Array<{
    id: string;
    title: string;
    isPdf: boolean;
    date: Date;
    src: string;
  }>;
  handleDelete: (id: string) => Promise<void>;
}> = ({ elements, handleDelete }) => {
  const handleClick = (src: string) => {
    window.open(src, "_blank");
  };

  const table = useTable({
    data: elements,
    columns: getDocumentColumns(handleDelete, handleClick),
  });

  return (
    <div className="w-full">
      <DataTable table={table} />
    </div>
  );
};

const LastDocuments: FC<LastDocumentsProps> = ({
  patient,
  isImages,
  isTable,
  isPeriotools,
}) => {
  const { data, isLoading } = usePatientDocuments(
    patient.id.toString(),
    isPeriotools
  );
  const signedDocuments = useMemo(() => {
    if (isLoading || !data) return [];

    return data.map((signedDocument) => ({
      id: signedDocument.id,
      title: signedDocument.title,
      isPdf: signedDocument.isPdf,
      date: new Date(signedDocument.createdAt),
      src: signedDocument.s3SignedUrl,
    }));
  }, [data, isLoading]);

  const deleteMutation = useDeleteDocument();

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  if (isTable) {
    return (
      <TableComponent
        elements={signedDocuments.filter((elem) => elem.isPdf === !isImages)}
        handleDelete={handleDelete}
      />
    );
  }
  if (isImages) {
    return (
      <>
        <EmptyImageCard patientId={patient.id} isPeriotools={isPeriotools} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
          {signedDocuments
            .filter((elem) => !elem.isPdf)
            .map((image) => (
              <div key={image.src}>
                <img
                  src={image.src}
                  alt={"Document preview"}
                  className="object-contain h-full pt-8"
                />
              </div>
            ))}
        </div>
      </>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-4">
      {signedDocuments
        .filter((elem) => elem.isPdf)
        .map((document) => (
          <DocumentCard
            key={document.id}
            title={document.title}
            date={document.date}
            document={
              <div
                style={{ height: "100%", width: "auto", overflow: "hidden" }}
              >
                <PdfPreview key={document.src} url={document.src} />
              </div>
            }
            src={document.src}
          />
        ))}
    </div>
  );
};

export default LastDocuments;
